import Button from '@orn/shared-web/components/Button/Button';
import SocialButton from '@orn/shared-web/components/Button/SocialButton';
import { HookForm, FormField } from '@orn/shared-web/components/Form';
import Input from '@orn/shared-web/components/Input';
import { ArrowRightIcon, MailIcon } from '@orn/shared-web/icons';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import useAuthApi from '../../../hooks/useAuthApi';
import { getApiBaseUrl } from '../../../lib/getEnv';
import validateEmail from '../../../lib/validation/emailValidation';
import LoginHeader from '../LoginHeader';
import LoginTitle from '../LoginTitle';

import type { CheckEmailResponse } from '../../../hooks/useAuthApi';

interface IEmailEntryStepProps {
  onNext: (result: { email: string; checkResult: CheckEmailResponse }) => void;
}

interface IEmailFormValues {
  email: string;
}

const isEmailValid = (email: string): boolean => {
  return validateEmail(email.trim()).isValid;
};

const EmailEntryStep = ({ onNext }: IEmailEntryStepProps) => {
  const authApi = useAuthApi();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const form = useForm<IEmailFormValues>({
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  const emailValue = form.watch('email');

  const handleGoogleSignIn = () => {
    window.location.href = `${getApiBaseUrl()}/auth/google`;
  };

  const handleSubmit = React.useCallback(
    async (values: IEmailFormValues) => {
      const trimmed = values.email.trim();
      const validation = validateEmail(trimmed);
      if (!validation.isValid) {
        form.setError('email', { message: validation.error });
        return;
      }

      setIsSubmitting(true);
      const result = await authApi.checkEmail({ email: trimmed });
      setIsSubmitting(false);

      if (!result.ok) {
        form.setError('email', { message: result.error.error });
        return;
      }

      onNext({ email: trimmed, checkResult: result.data });
    },
    [authApi, form, onNext]
  );

  return (
    <>
      <LoginHeader />
      <LoginTitle title="Welcome to Mantle" />

      <HookForm form={form} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex w-full flex-col gap-4">
          <FormField control={form.control} name="email">
            {({ field, fieldState }) => (
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    {...field}
                    type="email"
                    autoFocus
                    label="Email address"
                    placeholder="Enter your email"
                    icon={MailIcon}
                    size="md"
                    isInvalid={Boolean(fieldState.error)}
                    hint={fieldState.error?.message}
                    autoComplete="email"
                  />
                </div>
                <Button
                  type="submit"
                  size="md"
                  color="primary"
                  iconLeading={ArrowRightIcon}
                  isDisabled={!isEmailValid(emailValue)}
                  isLoading={isSubmitting}
                  aria-label="Continue"
                />
              </div>
            )}
          </FormField>
        </div>
      </HookForm>

      {/* Divider */}
      <div className="flex w-full items-center gap-4 py-2">
        <div className="bg-secondary h-px flex-1" />
        <span className="text-tertiary text-sm">or</span>
        <div className="bg-secondary h-px flex-1" />
      </div>

      {/* Google Sign In */}
      <div className="flex w-full">
        <SocialButton size="xl" social="google" onClick={handleGoogleSignIn} className="w-full">
          Sign in with Google
        </SocialButton>
      </div>
    </>
  );
};

EmailEntryStep.displayName = 'EmailEntryStep';

export default EmailEntryStep;
